/* eslint-disable react/react-in-jsx-scope */
import type { Entity } from '@backstage/catalog-model';
import { stringifyEntityRef } from '@backstage/catalog-model';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import TextField from '@material-ui/core/TextField';
import { makeStyles } from '@material-ui/core/styles';
import Check from '@material-ui/icons/Check';
import Close from '@material-ui/icons/Close';
import Search from '@material-ui/icons/Search';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import { useEffect, useRef, useState } from 'react';
import { useCatalogEntitySearch } from './useCatalogEntitySearch';

const useStyles = makeStyles(theme => ({
  listWrap: {
    maxHeight: 320,
    overflow: 'auto',
  },
  emptyHint: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
}));

type Props = {
  open: boolean;
  onClose: () => void;
  value: Entity | null;
  onChange: (entity: Entity | null) => void;
};

function entityRefsEqual(a: Entity | null, b: Entity | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return stringifyEntityRef(a) === stringifyEntityRef(b);
}

export function EntityCatalogPickerDialog({
  open,
  onClose,
  value,
  onChange,
}: Props) {
  const classes = useStyles();
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { value: options = [], loading } = useCatalogEntitySearch(search);

  useEffect(() => {
    if (open) {
      setSearch('');
      const id = requestAnimationFrame(() => {
        searchInputRef.current?.focus();
      });
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [open]);

  const q = search.trim();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="entity-picker-title"
    >
      <DialogTitle id="entity-picker-title">
        Select an entity
        <IconButton
          aria-label="close"
          onClick={onClose}
          style={{ position: 'absolute', right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          variant="outlined"
          margin="dense"
          placeholder="Search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          inputRef={searchInputRef}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: loading ? (
              <InputAdornment position="end">
                <CircularProgress color="inherit" size={20} />
              </InputAdornment>
            ) : null,
          }}
          inputProps={{ 'aria-label': 'Search catalog entities' }}
        />
        <div className={classes.listWrap}>
          {q.length < 2 && (
            <Typography variant="body2" className={classes.emptyHint}>
              Type at least 2 characters to search
            </Typography>
          )}
          {q.length >= 2 && !loading && options.length === 0 && (
            <Typography variant="body2" className={classes.emptyHint}>
              No entities found
            </Typography>
          )}
          {q.length >= 2 && !loading && options.length > 0 && (
            <List dense disablePadding>
              {options.map(e => {
                const selected = entityRefsEqual(value, e);
                return (
                  <ListItem
                    key={stringifyEntityRef(e)}
                    button
                    selected={selected}
                    onClick={() => {
                      onChange(e);
                      onClose();
                    }}
                  >
                    <ListItemIcon>
                      {selected ? (
                        <Check fontSize="small" />
                      ) : (
                        <span style={{ width: 24 }} />
                      )}
                    </ListItemIcon>
                    <ListItemText primary={stringifyEntityRef(e)} />
                  </ListItem>
                );
              })}
            </List>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
